import {
  Code2,
  Table2,
  HardDrive,
  Gauge,
  KeyRound,
  Shield,
  GitCompare,
  Terminal,
  Zap,
  Eye,
  Lock,
} from "lucide-react";

const features = [
  {
    icon: Code2,
    title: "Monaco Query Editor",
    description: "Write queries with full IntelliSense, syntax highlighting, and auto-completion powered by the same editor as VS Code.",
    size: "large",
  },
  {
    icon: Table2,
    title: "Visual Data Grid",
    description: "Browse, edit, and manage your data in a powerful, spreadsheet-like interface with inline editing, Table Designer, and real-time metrics.",
    size: "medium",
  },
  {
    icon: HardDrive,
    title: "Backup Manager",
    description: "Schedule automatic backups and restore your databases with a single click.",
    size: "medium",
  },
  {
    icon: Gauge,
    title: "Performance Advisor",
    description: "Get real-time insights and optimization suggestions to keep your databases running at peak performance.",
    size: "large",
  },
  {
    icon: KeyRound,
    title: "Secrets Manager",
    description: "Securely store and manage database credentials with encrypted storage.",
    size: "medium",
  },
  {
    icon: Shield,
    title: "SSH & SSL Support",
    description: "Connect securely through SSH tunnels and SSL/TLS encrypted connections.",
    size: "small",
  },
  {
    icon: GitCompare,
    title: "Change Review",
    description: "Review and approve changes before they're applied to your databases.",
    size: "medium",
  },
];

const additionalFeatures = [
  { icon: Terminal, label: "Query History" },
  { icon: Zap, label: "Fast Execution" },
  { icon: Eye, label: "Schema Visualization" },
  { icon: Lock, label: "Accurate Metrics" },
];

export function Features() {
  return (
    <section id="features" className="relative py-24 px-6">
      <div className="gradient-orb gradient-orb-3" />
      
      <div className="max-w-6xl mx-auto">
        {/* Section Header */}
        <div className="text-center mb-16">
          <p className="text-sm font-semibold text-emerald-400 uppercase tracking-wider mb-4">
            Powerful Features
          </p>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Everything You Need
          </h2>
          <p className="text-zinc-400 max-w-xl mx-auto">
            A comprehensive toolkit for database management, built with modern developers in mind.
          </p>
        </div>

        {/* Bento Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4 md:gap-6 mb-12">
          {features.map((feature, index) => {
            const Icon = feature.icon;
            const isLarge = feature.size === "large";
            
            return (
              <div
                key={feature.title}
                className={`bento-card opacity-0 animate-fade-in-up ${
                  isLarge ? "lg:col-span-2" : ""
                }`}
                style={{ 
                  animationDelay: `${index * 75}ms`,
                  animationFillMode: 'forwards'
                }}
              >
                <div className="feature-icon mb-4">
                  <Icon className="w-6 h-6 text-emerald-400" />
                </div>
                <h3 className="text-lg font-semibold text-white mb-2">
                  {feature.title}
                </h3>
                <p className="text-sm text-zinc-400 leading-relaxed">
                  {feature.description}
                </p>
              </div>
            );
          })}
        </div>

        {/* Additional Features Row */}
        <div className="flex flex-wrap justify-center gap-4">
          {additionalFeatures.map((feature, index) => {
            const Icon = feature.icon;
            return (
              <div
                key={feature.label}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-white/5 border border-white/10 text-sm text-zinc-400 opacity-0 animate-fade-in"
                style={{ 
                  animationDelay: `${600 + index * 100}ms`,
                  animationFillMode: 'forwards'
                }}
              >
                <Icon className="w-4 h-4 text-emerald-400" />
                {feature.label}
              </div>
            );
          })}
        </div>
      </div>
    </section>
  );
}

